// [2026-09-08] ADDED — what an unauthenticated visitor sees instead of a password form.
//
// WHY THIS REPLACED THE FORM
// --------------------------
// This console is on a public URL and a staff session reads and writes EVERY tenant's data. Until
// now that URL offered an email-and-password box to anyone who opened it, which meant every named
// staff account was a second credential — with its own reset story, its own rotation story and its
// own leak story — duplicating an InCruiter account that already exists and is already managed by
// somebody. Since the operator bridge works (2026-09-08), staff arrive already authenticated by
// InCruiter. A second door that grants the same access is not redundancy; it is a second thing to
// steal.
//
// THE FORM IS NOT DELETED. AdminLogin.jsx is untouched and still imported — see the `#recover`
// note below and in AdminApp.jsx. Deleting it would throw away the only way to sign in to a
// deployment whose bridge is misconfigured.
//
// THIS SCREEN IS NOT THE SECURITY CONTROL, and must not be mistaken for one. Hiding a form stops
// nobody from POSTing to the API. The control is server-side: proctoring-sdk-backend refuses a
// named account's password when GATEWAY_ADMIN_PASSWORD_LOGIN_ENABLED is false
// (app/modules/admin/route.py). This screen exists so the page tells the truth about how to get in,
// rather than presenting a form that will always be refused.

// [2026-09-08] ADDED — the resolver lives in its own file with no import.meta.env in it, so it can
// be unit-tested under plain node. A pure function reading build-time globals cannot be.
import { resolveInCruiterAdminUrl, resolveInCruiterLogoutUrl } from './signInTarget.js'

// The InCruiter admin panel this console is paired with. BUILD-TIME, like VITE_API_BASE beside it.
//
// DELIBERATELY NO DEFAULT. Guessing a hostname per environment is how a dev console ends up
// pointing staff at the production panel, or at a host that does not resolve. Unset simply means
// the button is not rendered and the instruction stands on its own — which is still correct, just
// one click less convenient.
//
//   dev: VITE_INCRUITER_ADMIN_URL=https://dev.v1.admin.incruiter.com/proctoring
// [2026-09-08] CHANGED — exported, because AdminApp now redirects to it rather than only linking.
// OLD: const ADMIN_PANEL_URL = ...
//
// [2026-09-08] CHANGED AGAIN — derive it when it is not configured.
//
// OLD: export const ADMIN_PANEL_URL = (import.meta.env?.VITE_INCRUITER_ADMIN_URL || '').trim()
//
// THE COMMENT ABOVE ARGUED FOR NO DEFAULT and the argument was right about the risk and wrong about
// the outcome. dev was built without VITE_INCRUITER_ADMIN_URL — verified 2026-09-08 by reading the
// deployed bundle, which contains the redirect code and no admin URL at all — so the console
// silently did not redirect and served the static screen. A feature that is off unless somebody
// remembers a build argument is off.
//
// resolveInCruiterAdminUrl keeps the explicit value winning and falls back to a derivation narrow
// enough that it cannot cross environments. Its own file carries the full argument.
export const ADMIN_PANEL_URL = resolveInCruiterAdminUrl(
  import.meta.env?.VITE_INCRUITER_ADMIN_URL,
  import.meta.env?.VITE_API_BASE,
  typeof window !== 'undefined' ? window.location.origin : '',
)

// [2026-09-08] ADDED — where Sign out goes. Same origin as above, different page: it ends the
// InCruiter session rather than offering to start one. See resolveInCruiterLogoutUrl.
export const INCRUITER_LOGOUT_URL = resolveInCruiterLogoutUrl(
  import.meta.env?.VITE_INCRUITER_ADMIN_URL,
  import.meta.env?.VITE_API_BASE,
  typeof window !== 'undefined' ? window.location.origin : '',
)

// [2026-09-08] ADDED — the redirect loop guard.
//
// AdminApp sends an unauthenticated visitor to the InCruiter admin panel automatically. That panel
// answers by signing them in and sending them straight back here. If the session does not survive
// the round trip — a browser refusing the cookie, an operator whose account is not accepted, a
// clock skew expiring it on arrival — the two pages hand each other back and forth forever, and a
// redirect loop is a far worse failure than the screen it replaced: no error, no way to read
// anything, and a browser pinned at 100% CPU.
//
// So a bounce is RECORDED BEFORE IT HAPPENS and this window is respected on arrival. One automatic
// redirect per window; the second time we stay put and explain. sessionStorage, not a URL
// parameter, so a bookmarked or shared link cannot carry the "already tried" state and silently
// disable the redirect for somebody else.
const BOUNCE_KEY = 'psdk.admin.bounced-at'
const BOUNCE_WINDOW_MS = 30_000

export function recentlyBounced() {
  try {
    const at = Number(sessionStorage.getItem(BOUNCE_KEY) || 0)
    return at > 0 && Date.now() - at < BOUNCE_WINDOW_MS
  } catch {
    // Private browsing with storage denied. Returning TRUE means "assume we already bounced", so a
    // browser we cannot keep a note in never enters the loop — it sees this screen instead. The
    // safe direction is one fewer redirect, never one more.
    return true
  }
}

export function markBounced() {
  try { sessionStorage.setItem(BOUNCE_KEY, String(Date.now())) } catch { /* see above */ }
}

export function clearBounced() {
  try { sessionStorage.removeItem(BOUNCE_KEY) } catch { /* nothing to clear */ }
}

export default function BridgeSignIn({ onRecover, bounced }) {
  return (
    <div className="login-wrap">
      <div className="login-card" style={{ maxWidth: 440 }}>
        <div className="brand" style={{ padding: '0 0 6px' }}>
          <span className="brand-mark">In</span><span>InC-Proctor — Staff</span>
        </div>
        <h1 style={{ fontSize: 22, margin: '10px 0 4px' }}>
          {bounced ? 'Could not sign you in' : 'Sign in from InCruiter'}
        </h1>
        {/* [2026-09-08] CHANGED — two messages, because there are two genuinely different
            situations and one wording for both is how someone ends up retrying a thing that
            cannot work. `bounced` means we ALREADY sent them to InCruiter and they came back
            still unauthenticated, so "go to InCruiter" is precisely the advice that just failed. */}
        {bounced ? (
          <p className="muted" style={{ margin: '0 0 18px', fontSize: 13.5, lineHeight: 1.55 }}>
            You were sent to the InCruiter admin panel and returned without a session. Your
            InCruiter sign-in may have expired, or your account may not have InC-Proctor access.
            Sign in to InCruiter again, then click <strong>InC-Proctor</strong>.
          </p>
        ) : (
          <p className="muted" style={{ margin: '0 0 18px', fontSize: 13.5, lineHeight: 1.55 }}>
            This console does not have its own password. Open the InCruiter admin panel and click
            <strong> InC-Proctor</strong> in the sidebar — you will arrive here already signed in.
          </p>
        )}

        {ADMIN_PANEL_URL && (
          <a
            href={ADMIN_PANEL_URL}
            className="btn btn-primary"
            style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
          >
            Open the InCruiter admin panel
          </a>
        )}

        {/* [2026-09-08] CHANGED — hidden when `bounced`, where the paragraph above already says
            this and better. Two near-identical hints stacked on one card read as a broken page. */}
        {!bounced && (
          <p className="muted" style={{ margin: '18px 0 0', fontSize: 12.5, lineHeight: 1.5 }}>
            Already clicked through and still seeing this? Your InCruiter session may have expired —
            sign in to InCruiter again, then click InC-Proctor.
          </p>
        )}

        {/* THE BREAK-GLASS, and the reason it is a plain button rather than a hidden URL trick.
            A recovery path nobody can find is not a recovery path. It is unlabelled as "login" on
            purpose: it opens the password form, and the server will refuse every named account
            there — only `operator` + GATEWAY_OPERATOR_SECRET still passes. So this reveals a form
            that grants nothing to anyone who does not already hold the deployment secret. */}
        {typeof onRecover === 'function' && (
          <button
            type="button"
            onClick={onRecover}
            className="btn-link muted"
            style={{
              display: 'block', margin: '14px auto 0', background: 'none', border: 0,
              padding: 0, fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            Recovering a deployment? Staff password sign-in
          </button>
        )}
      </div>
    </div>
  )
}
